import { addItem, consumeItem, countItem, type StackLike, type StackSizeOf } from "./inventory.ts";

/** Forma mínima de uma receita (espelha `Recipe` de libs/content). */
export interface RecipeLike {
  id: string;
  output: string;
  qty: number;
  station: string;
  inputs: readonly { item: string; qty: number }[];
  key?: string;
}

export interface Missing {
  item: string;
  need: number;
  have: number;
}

/** Até 10 fabricações por pedido (doc 03). */
export const MAX_CRAFT_TIMES = 10;

/** Ingredientes que faltam para fabricar `times` vezes. */
export function missingFor(slots: readonly StackLike[], recipe: RecipeLike, times: number): Missing[] {
  const missing: Missing[] = [];
  for (const input of recipe.inputs) {
    const need = input.qty * times;
    const have = countItem(slots, input.item);
    if (have < need) missing.push({ item: input.item, need, have });
  }
  return missing;
}

/** A saída de `times` fabricações cabe depois de consumir os ingredientes? (testado numa cópia) */
function fits(slots: readonly StackLike[], recipe: RecipeLike, times: number, stackOf: StackSizeOf): boolean {
  const trial = slots.map((s) => ({ itemId: s.itemId, qty: s.qty }));
  for (const input of recipe.inputs) consumeItem(trial, input.item, input.qty * times);
  return addItem(trial, recipe.output, recipe.qty * times, stackOf) === 0;
}

/**
 * Quantas vezes dá para fabricar agora (até MAX_CRAFT_TIMES): pelos ingredientes e, com
 * `stackOf`, também pelo espaço para a saída — o mesmo que `craft` aceitaria.
 */
export function maxCraftable(slots: readonly StackLike[], recipe: RecipeLike, stackOf?: StackSizeOf): number {
  let times = MAX_CRAFT_TIMES;
  for (const input of recipe.inputs) times = Math.min(times, Math.floor(countItem(slots, input.item) / input.qty));
  if (!stackOf) return Math.max(0, times);
  while (times > 0 && !fits(slots, recipe, times, stackOf)) times--;
  return Math.max(0, times);
}

export type CraftResult = { ok: true; produced: number } | { ok: false; reason: string; missing?: Missing[] };

/**
 * Fabrica `times` vezes: confere os ingredientes e o espaço para a saída **antes** de mexer em
 * qualquer coisa (numa cópia), depois consome exatamente o necessário e entrega o produto.
 */
export function craft(slots: StackLike[], recipe: RecipeLike, times: number, stackOf: StackSizeOf): CraftResult {
  if (!Number.isInteger(times) || times < 1 || times > MAX_CRAFT_TIMES) return { ok: false, reason: "quantidade inválida" };
  const missing = missingFor(slots, recipe, times);
  if (missing.length > 0) return { ok: false, reason: "faltam ingredientes", missing };
  if (!fits(slots, recipe, times, stackOf)) return { ok: false, reason: "sem espaço no inventário" };
  for (const input of recipe.inputs) consumeItem(slots, input.item, input.qty * times);
  addItem(slots, recipe.output, recipe.qty * times, stackOf);
  return { ok: true, produced: recipe.qty * times };
}

/**
 * Receita descoberta (doc 03): estação conhecida (mãos sempre) e todos os ingredientes já vistos,
 * ou só o ingrediente-chave quando a receita define um.
 */
export function isDiscovered(recipe: RecipeLike, known: (itemId: string) => boolean, stationItem: string | undefined): boolean {
  if (stationItem !== undefined && !known(stationItem)) return false;
  if (recipe.key) return known(recipe.key);
  return recipe.inputs.every((i) => known(i.item));
}
